import { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, CheckCircle2, AlertTriangle, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';


export function MoneyStorageCensusPanel() {
  const [censusLoading, setCensusLoading] = useState(false);
  const [census, setCensus] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const pollRef = useRef(null);
  const [drift, setDrift] = useState(null);
  const [driftRunning, setDriftRunning] = useState(false);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(null);

  const runDrill = async (collection, field) => {
    setDrillLoading(`${collection}.${field}`);
    setDrill(null);
    try {
      const { data } = await api.get('/admin/diagnostics/money-storage-field-diff', {
        params: { collection, field },
      });
      setDrill(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDrillLoading(null);
    }
  };

  const runDriftCheck = async () => {
    setDriftRunning(true);
    try {
      const { data } = await api.post('/admin/diagnostics/money-storage-drift/run');
      setDrift(data);
      if (data.anomaly_count === 0) {
        toast.success('Drift check: no numeric money values detected');
      } else {
        toast.warning(`Drift check: ${data.anomaly_count} numeric value(s) detected`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDriftRunning(false);
    }
  };

  const runCensus = async () => {
    setCensusLoading(true);
    setCensus(null);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/money-storage-census');
      setCensus(data);
      // Also fetch drift status
      try {
        const { data: driftData } = await api.get('/admin/diagnostics/money-storage-drift');
        setDrift(driftData);
      } catch (_) { /* drift may not have run yet */ }

      if (data.healthy) {
        toast.success('All money fields store canonical strings');
      } else {
        toast.warning(`${data.global_numeric_count} numeric money value(s) detected`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCensusLoading(false);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/money-storage-normalize/preview');
      setPreview(data);
      if (data.global_count === 0) {
        toast.success('No documents need normalization');
      } else {
        toast.info(`${data.global_count} value(s) would be normalized`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Chunked apply as a background job: start, then poll to completion ──
  // The synchronous apply timed out over tens of thousands of values; the work
  // now runs chunked in the background and this card polls its progress.

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleJob = (job) => {
    if (!job) return;
    setApplyProgress(job.progress || null);
    if (job.status === 'completed') {
      stopPolling();
      setApplying(false);
      setApplyResult(job.result);
      setPreview(null);
      const rw = (job.result && job.result.global_rewritten) || 0;
      if (rw > 0) {
        toast.success(`Normalized ${rw} value(s) to canonical strings`);
      } else {
        toast.info('No changes (already normalized)');
      }
    } else if (job.status === 'failed') {
      stopPolling();
      setApplying(false);
      toast.error(job.error || 'Apply job failed');
    } else {
      setApplying(true);
    }
  };

  const pollOnce = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/diagnostics/money-storage-normalize/apply-status/${jobId}`);
      handleJob(data);
    } catch (err) {
      stopPolling();
      setApplying(false);
      toast.error(getErrorMessage(err));
    }
  };

  const startPolling = (jobId) => {
    stopPolling();
    setApplying(true);
    pollOnce(jobId);
    pollRef.current = setInterval(() => pollOnce(jobId), 4000);
  };

  const runApply = async () => {
    setApplying(true);
    setApplyResult(null);
    setApplyProgress(null);
    try {
      const { data } = await api.post('/admin/diagnostics/money-storage-normalize/apply-start');
      if (data.status === 'already_running') {
        toast.info('An apply job is already running; attaching to it');
      }
      startPolling(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setApplying(false);
    }
  };

  // Reload recovery: if a job is running (or recently completed), pick it back up.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/admin/diagnostics/money-storage-normalize/apply-latest');
        if (cancelled || !data || !data.status) return;
        if (data.status === 'running') {
          startPolling(data._id);
        } else if (data.status === 'completed') {
          setApplyResult(data.result);
          setApplyProgress(data.progress || null);
        }
      } catch (_) { /* no apply job has run yet */ }
    })();
    return () => { cancelled = true; stopPolling(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Database className="h-5 w-5" />
          Money Storage Types
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Scans all registered money fields for BSON type distribution. Money must be stored
          as canonical decimal strings. Numeric (double/int) storage is a migration target.
        </p>

        {/* Button row */}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={runCensus} disabled={censusLoading}>
            {censusLoading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Run Census
          </Button>
          <Button size="sm" variant="outline" onClick={runPreview} disabled={previewLoading}>
            {previewLoading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Preview Normalization
          </Button>
          <Button size="sm" variant="outline" onClick={runDriftCheck} disabled={driftRunning}>
            {driftRunning && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Run Drift Check
          </Button>
        </div>

        {/* Drift status */}
        {drift && drift.anomaly_count !== null && (
          <div className="flex items-center gap-2 text-sm">
            {drift.anomaly_count === 0 ? (
              <Badge variant="outline" className="text-green-700 border-green-300">Drift: 0</Badge>
            ) : (
              <Badge variant="destructive">Drift: {drift.anomaly_count}</Badge>
            )}
            <span className="text-muted-foreground">
              Last check: {drift.checked_at ?? 'never'}
            </span>
          </div>
        )}

        {/* Census results */}
        {census && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {census.healthy ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-500" />
              )}
              <span className="text-sm font-medium">
                {census.healthy
                  ? 'All money fields are canonical strings'
                  : `${census.global_numeric_count} numeric value(s) across ${census.registry_collection_count} collections`
                }
              </span>
            </div>

            <div className="text-xs text-muted-foreground">
              Registry: {census.registry_field_count} fields across {census.registry_collection_count} collections
            </div>

            {/* Per-collection breakdown */}
            {Object.entries(census.collections).map(([collName, collData]) => (
              <div key={collName} className="border rounded p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{collName}</span>
                  {collData.healthy ? (
                    <Badge variant="outline" className="text-green-700 border-green-300 text-xs">OK</Badge>
                  ) : (
                    <Badge variant="destructive" className="text-xs">
                      {collData.total_numeric_fields} numeric
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(collData.fields).map(([fieldName, fieldData]) => (
                    <div key={fieldName} className="text-xs bg-muted/50 rounded p-2">
                      <div className="font-mono font-medium">{fieldName}</div>
                      <div className="mt-1 space-y-0.5">
                        {Object.entries(fieldData.by_type).map(([bsonType, count]) => (
                          <div key={bsonType} className="flex justify-between">
                            <span className={
                              bsonType === 'double' || bsonType === 'int' || bsonType === 'long'
                                ? 'text-amber-600 font-medium'
                                : ''
                            }>
                              {bsonType}
                            </span>
                            <span>{count.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                      {fieldData.inconsistency && (
                        <div className="mt-1 text-red-600">{fieldData.inconsistency}</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Per-provider breakdown if multiple providers */}
                {Object.keys(collData.fields).length > 0 &&
                  Object.values(collData.fields).some(f =>
                    Object.keys(f.by_provider).length > 1
                  ) && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">
                      Per-provider breakdown
                    </summary>
                    <div className="mt-2 space-y-2">
                      {Object.entries(collData.fields).map(([fieldName, fieldData]) => (
                        Object.keys(fieldData.by_provider).length > 1 && (
                          <div key={fieldName}>
                            <div className="font-mono font-medium mb-1">{fieldName}</div>
                            {Object.entries(fieldData.by_provider).map(([provider, types]) => (
                              <div key={provider} className="ml-2 flex gap-2">
                                <span className="text-muted-foreground w-24 truncate">{provider}</span>
                                {Object.entries(types).map(([t, c]) => (
                                  <span key={t} className={
                                    t === 'double' || t === 'int' ? 'text-amber-600' : ''
                                  }>
                                    {t}:{c}
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        )
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Preview results */}
        {preview && (
          <div className="space-y-3 border-t pt-3">
            <h4 className="text-sm font-semibold">Normalization Preview</h4>

            {preview.global_count === 0 ? (
              <p className="text-sm text-green-600">No changes needed. All money fields are already canonical strings.</p>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm">
                  {preview.global_checksums_match ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                  )}
                  <span>
                    {preview.global_count} value(s) to normalize.
                    Checksums: {preview.global_checksums_match ? 'RECONCILED' : 'MISMATCH (do not apply)'}
                    {preview.max_checksum_delta != null && (
                      <span className="text-muted-foreground">
                        {' '}(max delta {preview.max_checksum_delta}
                        {preview.max_checksum_delta_field ? ' at ' + preview.max_checksum_delta_field : ''})
                      </span>
                    )}
                  </span>
                </div>

                {/* Per-collection preview */}
                {Object.entries(preview.collections).map(([collName, collPreview]) => (
                  collPreview.total_to_rewrite > 0 && (
                    <div key={collName} className="border rounded p-2 space-y-1">
                      <div className="text-sm font-medium">{collName}: {collPreview.total_to_rewrite} values</div>
                      {Object.entries(collPreview.fields).map(([fieldName, fp]) => (
                        fp.count > 0 && (
                          <div key={fieldName} className="text-xs ml-2">
                            <span className="font-mono">{fieldName}</span>: {fp.count} values
                            {fp.checksums_match ? (
                              <Badge variant="outline" className="ml-2 text-green-700 border-green-300 text-xs">checksum OK</Badge>
                            ) : (
                              <Badge variant="destructive" className="ml-2 text-xs">checksum MISMATCH</Badge>
                            )}
                            {fp.checksum_delta != null && (
                              <span className="ml-2 text-muted-foreground font-mono">
                                delta {fp.checksum_delta} / tol {fp.checksum_tolerance}
                              </span>
                            )}
                            {fp.reconcile_population != null && (
                              <span className="ml-2 text-muted-foreground">
                                reconciled over {fp.reconcile_population.toLocaleString()} value(s)
                                {fp.reconcile_skipped > 0 && `, ${fp.reconcile_skipped} unparseable skipped`}
                              </span>
                            )}
                            {!fp.checksums_match && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="ml-2 h-5 px-2 text-[10px]"
                                onClick={() => runDrill(collName, fieldName)}
                                disabled={drillLoading === `${collName}.${fieldName}`}
                              >
                                {drillLoading === `${collName}.${fieldName}` && (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                )}
                                Drill down
                              </Button>
                            )}
                            {fp.non_round_trip_samples.length > 0 && (
                              <details className="mt-1">
                                <summary className="cursor-pointer text-muted-foreground">
                                  {fp.non_round_trip_samples.length} representation change(s)
                                </summary>
                                <div className="mt-1 font-mono bg-muted rounded p-1">
                                  {fp.non_round_trip_samples.map((s, i) => (
                                    <div key={i}>{s.raw} → {s.canonical}</div>
                                  ))}
                                </div>
                              </details>
                            )}
                            {fp.rewrite_samples && fp.rewrite_samples.length > 0 && (
                              <details className="mt-1">
                                <summary className="cursor-pointer text-muted-foreground">
                                  {fp.rewrite_samples.length} document(s) to normalize (named)
                                </summary>
                                <div className="mt-1 font-mono bg-muted rounded p-1">
                                  {fp.rewrite_samples.map((s, i) => (
                                    <div key={i} className="truncate" title={s._id}>
                                      {s._id}: {s.raw} → {s.canonical}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        )
                      ))}
                    </div>
                  )
                ))}

                {preview.safe_to_apply && (
                  <Button size="sm" variant="destructive" onClick={runApply} disabled={applying}>
                    {applying && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                    {applying ? 'Applying (background)...' : 'Apply Normalization'}
                  </Button>
                )}
                {!preview.safe_to_apply && (
                  <p className="text-sm text-red-600 font-medium">
                    Cannot apply: checksum delta {preview.max_checksum_delta}
                    {preview.max_checksum_delta_field ? ' at ' + preview.max_checksum_delta_field : ''}
                    {' '}exceeds the rounding tolerance. This is a genuine data mismatch, not float
                    noise. Investigate the named field before proceeding.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Drill-down results */}
        {drill && (
          <div className="space-y-2 border-t pt-3">
            <h4 className="text-sm font-semibold">
              Field diff: <span className="font-mono">{drill.collection}.{drill.field}</span>
            </h4>
            <div className="text-xs text-muted-foreground">
              {drill.total_numeric_docs.toLocaleString()} numeric doc(s), {drill.disagreeing_docs.toLocaleString()} disagree.
              {' '}Grand delta <span className="font-mono text-red-600">{drill.grand_delta}</span>
              {' '}(before {drill.grand_before} vs after {drill.grand_after}).
            </div>

            {/* Per BSON type totals */}
            <table className="text-xs w-full border-collapse">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-1 pr-2">bson type</th>
                  <th className="py-1 pr-2">count</th>
                  <th className="py-1 pr-2 text-right">before sum</th>
                  <th className="py-1 pr-2 text-right">after sum</th>
                  <th className="py-1 pr-2 text-right">delta</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(drill.by_bson_type).map(([t, v]) => (
                  <tr key={t} className="border-b border-border/30">
                    <td className="py-1 pr-2 font-mono">{t}</td>
                    <td className="py-1 pr-2 font-mono">{v.count}</td>
                    <td className="py-1 pr-2 font-mono text-right">{v.before_sum}</td>
                    <td className="py-1 pr-2 font-mono text-right">{v.after_sum}</td>
                    <td className={`py-1 pr-2 font-mono text-right ${Number(v.delta) !== 0 ? 'text-red-600' : ''}`}>{v.delta}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Top offending documents */}
            {drill.top_diffs.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="text-xs font-medium mt-1">Top {drill.top_diffs.length} by absolute difference (cap {drill.sample_cap})</div>
                <table className="text-xs w-full border-collapse mt-1">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-2">_id</th>
                      <th className="py-1 pr-2">type</th>
                      <th className="py-1 pr-2 text-right">before ($toDecimal)</th>
                      <th className="py-1 pr-2 text-right">after (Decimal(str))</th>
                      <th className="py-1 pr-2 text-right">difference</th>
                      <th className="py-1 pr-2">skip?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drill.top_diffs.map((r) => (
                      <tr key={r._id} className="border-b border-border/30">
                        <td className="py-1 pr-2 font-mono truncate max-w-[140px]" title={r._id}>{r._id}</td>
                        <td className="py-1 pr-2 font-mono">{r.bson_type}</td>
                        <td className="py-1 pr-2 font-mono text-right">{r.before ?? '(skipped)'}</td>
                        <td className="py-1 pr-2 font-mono text-right">{r.after ?? '(skipped)'}</td>
                        <td className="py-1 pr-2 font-mono text-right text-red-600">{r.difference}</td>
                        <td className="py-1 pr-2">
                          {r.one_side_skipped && (
                            <Badge variant="destructive" className="text-[10px]">skip</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No per-document disagreement in the numeric population. The delta is float
                representation noise, not lost/duplicated data.
              </p>
            )}
          </div>
        )}

        {/* Apply progress (background job polling) */}
        {applying && (
          <div className="space-y-1 border-t pt-3" data-testid="apply-progress">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Normalizing in the background...</span>
            </div>
            {applyProgress && (
              <div className="ml-6 text-xs text-muted-foreground space-y-0.5">
                <div>
                  Fields: {applyProgress.fields_done}/{applyProgress.fields_total}
                  {applyProgress.current_field ? ` (${applyProgress.current_field})` : ''}
                </div>
                <div>Values rewritten: {Number(applyProgress.values_rewritten || 0).toLocaleString()}</div>
                <div>Remaining: {Number(applyProgress.remaining || 0).toLocaleString()}</div>
                {applyProgress.errors > 0 && (
                  <div className="text-red-600">Errors: {applyProgress.errors}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Apply results */}
        {applyResult && !applying && (
          <div className="space-y-2 border-t pt-3" data-testid="apply-result">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">
                Applied: {applyResult.global_rewritten} value(s) normalized
                {applyResult.global_errors > 0 && (
                  <span className="text-red-600 ml-2">({applyResult.global_errors} errors)</span>
                )}
              </span>
            </div>
            {applyResult.global_remaining != null && (
              <div className="text-xs ml-6 text-muted-foreground">
                Preview re-run after apply: {Number(applyResult.global_remaining).toLocaleString()} value(s) still numeric
              </div>
            )}
            {applyResult.fields && Object.entries(applyResult.fields)
              .filter(([, v]) => v.rewritten > 0)
              .map(([name, v]) => (
                <div key={name} className="text-xs ml-4 font-mono">
                  {name}: {v.rewritten} rewritten{v.errors > 0 ? `, ${v.errors} errors` : ''}
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
